import dateService from 'live/services/date'
import VisualMarker from './visual'
import { Widget } from './visual/service'
import ApiMarkers, { QueryParams, MarkersData, MarkersAnnotation } from './api'

// TODO:
//   O Update annotation position
//   x Permissions (who can see those notes)

type OpenFormCallback = (
    marker: MarkersData,
    position: Record<string, unknown>,
    saveMarker: (marker: MarkersData) => Promise<void | MarkersData>,
    removeMarker: (marker: MarkersData) => Promise<void | Error>
) => void

type Hook = {
    widgetType: string
    test: (data: MarkersData | QueryParams, widget: Widget, config: Record<string, unknown>) => boolean
    beforeSave: (marker: MarkersData, widget: Widget, config: Record<string, unknown>) => Promise<MarkersData>
    beforeList: (query: QueryParams, widget: Widget, config: Record<string, unknown>) => QueryParams
}

class Markers {
    public api: ApiMarkers
    public visual: VisualMarker
    private detailsMap: Record<number, OpenFormCallback | null> = {}
    private hooks: Hook[] = []
    private widgets: Widget[] = []
    private config: Record<string, unknown> = { type: 'live', merge: false }

    constructor() {
        this.api = new ApiMarkers()
        this.visual = new VisualMarker(this.save, this.api.remove, this.openDetailsFor)

        this.api.setListener({ onEvent: this.insert, onDelete: this.visual.remove })
        // If there are scheduled markers to be inserted in chart 01 after the marker
        // query for chart 01 restarts we need to clear all scheduled markers for chart 01.
        this.api.activeQueries.subscribe(items => {
            items
                .filter(change => change[1] != null && change[0]?.includes?.('+'))
                .forEach(change => this.visual.clearScheduledMarkers(change[1].widgetId))
        })
    }

    private openDetailsFor = (widgetId: number, marker: MarkersData, position: Record<string, unknown>): void => {
        const callback = this.detailsMap[widgetId]

        if (!callback) return

        callback(
            marker,
            position,
            data => {
                return this.save(data)
            },
            data => {
                return this.api.remove(data)
            }
        )
    }

    private save = async (data: MarkersData): Promise<MarkersData | void> => {
        const widgets = this.getTargetWdigets(data.target)

        for (const widget of widgets) {
            const hook = this.hooks.find(a => a.test(data, widget, this.config))

            if (hook) data = await hook.beforeSave(data, widget, this.config)
        }

        // Static markers are not editable
        if (data.__rawExtra && data.markerType === 'STATIC') {
            data.id = data.__rawExtra.id
            data.target = data.__rawExtra.target
            data.value = data.__rawExtra.value
            data.unit = data.__rawExtra.unit
            data.widgets = data.__rawExtra.widgets
            data.__rawExtra = null
        }

        return this.api.save(data)
    }

    private getTargetWdigets(target: MarkersData['target']): Widget[] {
        if (target.widgetId != null) return this.widgets.filter(a => a.options.id === target.widgetId)

        return this.widgets.filter(a => a.chart.spanType === target.spanType)
    }

    private insert = (marker: MarkersData): void => {
        const targets = this.getTargetWdigets(marker.target)

        // Updating labels marker order before inserting it.
        for (const widgetMarker of marker.widgets) {
            for (const point of (widgetMarker?.points ?? [])) {
                const widget = targets.find(a => a.options.id === widgetMarker.widgetId)
                point.index = (widget.cfg?.yAxis || []).find(a => a.__id === point.name)?.__index
            }
        }

        return this.visual.insert(marker)
    }

    /**
     * Register a callback for whenever a marker inside the
     * provided widget is clicked.
     *
     * @param widgetId The target widget
     * @param callback The action to take when a marker is clicked.
     *
     * @returns A unregister function
     */
    public onShowMarkerDetailsFor(widgetId: number, callback: OpenFormCallback): () => void {
        this.detailsMap[widgetId] = callback

        return () => (this.detailsMap[widgetId] = null)
    }

    /**
     * Checks if the query of a widget registred in `this.assemble(...)` needs
     * to be updated due to an span change.
     *
     * @param widget Widget that might have changed its span.
     *
     * @returns A updated QueryParams reflecting span changes.
     */
    public maybeUpdateQueryFor = (widget: Widget): QueryParams | null => {
        const context = widget?.options?.context
        const dashboardContext = widget?.options?.dashboardContext
        const dashboardWidgetContext = widget?.options?.dashboardWidgetContext

        let params: QueryParams = {
            widgetId: context?.widget?.id,
            dashboardId: context?.widget?.dashboard?.id,
            spanType: widget?.chart?.spanType,
            widgetType: widget?.context?.widget?.type,
            span: dashboardWidgetContext?.state?.params?.span,
            dashboardSpan: dashboardContext?.params || {},
            spanConfig: { spanDetached: false },
            lookupValues: dashboardContext.state?.currentFilterValues || dashboardContext.state?.filterValues,
            extra: {}
        }

        if (dashboardWidgetContext?.state?.widgetTransientParams?.spanDetached) {
            const trtParams = dashboardWidgetContext?.state?.widgetTransientParams

            params.span = trtParams.span
            params.spanConfig = trtParams?.spanPicker?.configs || {}
            params.spanConfig.spanDetached = true
        }

        if (!params.span || !params.widgetId || !params.dashboardId || !params.spanType) return null

        if (params.spanType === 'time') {
            params.span = dateService.prependTimezoneOffset(params.span)
        }

        const hook = this.hooks.find(a => a.test(params, widget, this.config))

        if (hook != null) {
            params = hook.beforeList(params, widget, this.config) as typeof params
        }

        this.api.activeQueries.add(params)

        return params
    }

    /**
     * Registries a widget to be able to show and create
     * markers.
     *
     * @param widget Widget that will be able to show and create markers
     * @returns A `undo` function that **must** be called on the widget is destroyed.
     */
    public assemble(widget: Widget): () => void {
        if (!widget?.chart?.xAxis) return () => null

        widget.chart.widgetId = widget.options.id as number
        widget.chart.widgetName = widget.options.name as string
        widget.chart.spanType = widget?.cfg?.spanPicker?.type || 'time'

        const params = this.maybeUpdateQueryFor(widget)

        if (params == null) return () => null

        const undo = this.visual.assemble(widget)

        const index = this.widgets.findIndex(a => a.options.id === widget.options.id)

        if (index !== -1) {
            this.widgets.splice(index, 1)
        }

        this.widgets.push(widget)

        return () => {
            this.api.activeQueries.remove(params)

            const index = this.widgets.findIndex(a => a.options.id === params.widgetId)

            if (index !== -1) this.widgets.splice(index, 1)

            undo()
        }
    }

    /**
     * Turns the marker mode on. While marker mode is on you can click
     * anywhere on chart to create a marker.
     *
     * @param widget Widget in which the marker mode should be turned on.
     */
    public turnMarkerModeOn(widget: Widget | string): void {
        this.visual.turnMarkerModeOn(widget)
    }

    /**
     * @deprecated Use `registerSaveAndListStrategyFor` instead.
     *
     * Register a strategy for saving a marker with custom span type.
     *
     * @param spanType Custom span type.
     * @param saveFn How to save markers with this custom span type.
     */
    public registerSaveMarkerStrategyFor(
        spanType: string,
        saveFn: (marker: MarkersAnnotation) => Promise<MarkersAnnotation | Error>
    ): void {
        this.api.registerSaveMarkerStrategyFor(spanType, saveFn)
    }

    /**
     * Register a custom strategy for saving and listing a marker of a given widget type.
     *
     * @param widgetType Custom widget type.
     * @param beforeSave How to save markers with this custom widget type.
     * @param beforeList How to list markers with this custom widget type.
     */
    public registerSaveAndListStrategyFor(
        widgetType: string,
        test: (data: MarkersData | QueryParams, widget: Widget, config: Record<string, unknown>) => boolean,
        beforeSave: (marker: MarkersData, widget: Widget) => Promise<MarkersData>,
        beforeList: (query: QueryParams, widget: Widget) => QueryParams
    ): void {
        if (this.hooks.find(a => a.widgetType === widgetType)) return

        const hook = { widgetType, test, beforeList, beforeSave }

        this.hooks.push(hook)
    }
}

export default new Markers()
